// Sculk spread (Deep Dark, M22.1). PURE (ENG-1): the "charge" model a
// sculk_catalyst runs when a mob dies nearby. The catalyst absorbs the death's
// experience as a budget of charge, then sculk creeps outward over the exposed
// surfaces around the corpse — each block it swallows costs exactly one charge.
// The headline invariant is conservation: every point of absorbed XP either
// becomes one sculk block or returns to the catalyst; nothing is created or lost
//   (`spent + remaining === charge`, `spent === convert.length`).
import { SCULK } from '../config';

export const SCULK_BLOCK = 'sculk';
export const SCULK_CATALYST = 'sculk_catalyst';

export interface Pos { readonly x: number; readonly y: number; readonly z: number; }

/** The world as the spread sees it: a block name at a cell, or null if the cell
 *  is outside the loaded region (treated as non-convertible). */
export interface SculkView {
  block(x: number, y: number, z: number): string | null;
}

/** The six face neighbours, in a fixed order so a bloom is fully deterministic. */
const NEIGHBORS: readonly Pos[] = [
  { x: 1, y: 0, z: 0 }, { x: -1, y: 0, z: 0 },
  { x: 0, y: 0, z: 1 }, { x: 0, y: 0, z: -1 },
  { x: 0, y: 1, z: 0 }, { x: 0, y: -1, z: 0 },
];

/** Blocks sculk will not swallow: empty/fluid cells and the sculk family itself. */
const NON_REPLACEABLE = new Set<string>(['air', 'water', 'lava', SCULK_BLOCK, SCULK_CATALYST]);

export function isSculk(name: string | null): boolean {
  return name === SCULK_BLOCK || name === SCULK_CATALYST;
}

/** A cell can turn to sculk if it holds a real (non-fluid, non-sculk) block with
 *  at least one face open to air/water — sculk only coats exposed surfaces. */
export function isConvertible(view: SculkView, x: number, y: number, z: number): boolean {
  const here = view.block(x, y, z);
  if (here === null || NON_REPLACEABLE.has(here)) return false;
  for (const n of NEIGHBORS) {
    const nb = view.block(x + n.x, y + n.y, z + n.z);
    if (nb === 'air' || nb === 'water') return true;
  }
  return false;
}

/** The XP a mob death offers a catalyst as spread charge (one charge per point). */
export function chargeFromXp(xp: number): number {
  return Math.max(0, Math.floor(xp));
}

/** The nearest sculk_catalyst within `range` (Chebyshev) of a death, or null. The
 *  catalyst is what absorbs the death's XP; without one, orbs drop normally. */
export function catalystNear(
  death: Pos,
  view: SculkView,
  range: number = SCULK.CATALYST_RANGE,
): Pos | null {
  let best: Pos | null = null;
  let bestD = Infinity;
  for (let dy = -range; dy <= range; dy++) {
    for (let dx = -range; dx <= range; dx++) {
      for (let dz = -range; dz <= range; dz++) {
        if (view.block(death.x + dx, death.y + dy, death.z + dz) !== SCULK_CATALYST) continue;
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bestD) { bestD = d; best = { x: death.x + dx, y: death.y + dy, z: death.z + dz }; }
      }
    }
  }
  return best;
}

/** The floor cell a bloom seeds from: the first convertible block at or just
 *  below the death (a mob dies standing on the surface sculk should coat). Null
 *  if there's nothing sculk-able underfoot within a short drop. */
export function bloomOrigin(death: Pos, view: SculkView): Pos | null {
  for (let dy = 0; dy <= SCULK.FLOOR_DROP; dy++) {
    const y = death.y - dy;
    if (isConvertible(view, death.x, y, death.z)) return { x: death.x, y, z: death.z };
  }
  return null;
}

export interface SculkBloom {
  /** Cells turned to sculk this bloom, in spread order (each cost one charge). */
  readonly convert: Pos[];
  /** Charge spent === convert.length. */
  readonly spent: number;
  /** Charge left over when the surface ran out before the budget did. */
  readonly remaining: number;
}

/**
 * Spread sculk outward from `origin`, spending up to `charge` (= absorbed XP).
 * A deterministic surface flood-fill (fixed neighbour order) swallows one
 * exposed block per charge until the budget or the reachable surface is
 * exhausted, bounded to `SCULK.SPREAD_RANGE` Chebyshev of the origin.
 *
 * Conserves charge exactly: `spent + remaining === floor(charge)` and
 * `spent === convert.length`.
 */
export function bloom(origin: Pos, charge: number, view: SculkView): SculkBloom {
  const budget = chargeFromXp(charge);
  const convert: Pos[] = [];
  if (budget <= 0) return { convert, spent: 0, remaining: budget };
  const range = SCULK.SPREAD_RANGE;
  const seen = new Set<string>();
  const key = (x: number, y: number, z: number): string => `${x},${y},${z}`;
  const queue: Pos[] = [];
  if (isConvertible(view, origin.x, origin.y, origin.z)) {
    queue.push(origin);
    seen.add(key(origin.x, origin.y, origin.z));
  }
  while (queue.length > 0 && convert.length < budget) {
    const c = queue.shift()!;
    convert.push(c);
    for (const n of NEIGHBORS) {
      const nx = c.x + n.x, ny = c.y + n.y, nz = c.z + n.z;
      if (Math.abs(nx - origin.x) > range || Math.abs(ny - origin.y) > range || Math.abs(nz - origin.z) > range) continue;
      const k = key(nx, ny, nz);
      if (seen.has(k)) continue;
      seen.add(k);
      if (isConvertible(view, nx, ny, nz)) queue.push({ x: nx, y: ny, z: nz });
    }
  }
  return { convert, spent: convert.length, remaining: budget - convert.length };
}
